import React from 'react';
import {
  Box, Button, ButtonText, Center, HStack, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { FaceDoDont } from './IdCaptureUiParts';

export type IdentityPhotoReviewStepProps = {
  documentImage: string;
  faceImage: string;
  onSend: () => void | Promise<void>;
  onRetakeDocument: () => void;
  onRetakeFace: () => void;
  isSending?: boolean;
  sendError?: string | null;
};

function PhotoPreview({
  image, aspect, label, height = 140,
}: { image: string; aspect: number; label: string; height?: number }) {
  return (
    <VStack space="xs" alignItems="center">
      <Box
        h={height}
        borderRadius="$md"
        overflow="hidden"
        bg="$bgSubtle"
        borderWidth={1}
        borderColor="$borderLight"
        style={{ aspectRatio: aspect }}
      >
        <img
          src={`data:image/jpeg;base64,${image}`}
          alt=""
          style={{
            width: '100%', height: '100%', objectFit: 'cover', display: 'block',
          }}
        />
      </Box>
      <Text size="sm" color="$textSubtle">{label}</Text>
    </VStack>
  );
}

/**
 * Check step — both identity photos side by side before one guarded send.
 */
export function IdentityPhotoReviewStep({
  documentImage,
  faceImage,
  onSend,
  onRetakeDocument,
  onRetakeFace,
  isSending = false,
  sendError = null,
}: IdentityPhotoReviewStepProps) {
  const { t } = useTranslation();

  return (
    <Center flex={1} p="$6" testID="oral-exam-id-capture-step-check">
      <VStack space="lg" maxWidth={560} width="100%" alignItems="center">
        <VStack space="sm" alignItems="center">
          <Text size="xl" fontFamily="$heading" color="$textHeading" textAlign="center">
            {t('oralExam.idCapture.review.title')}
          </Text>
          <Text size="md" textAlign="center">{t('oralExam.idCapture.review.body')}</Text>
        </VStack>

        <HStack space="lg" justifyContent="center">
          <PhotoPreview
            image={faceImage}
            aspect={0.75}
            label={t('oralExam.idCapture.review.faceLabel')}
            height={180}
          />
          <PhotoPreview
            image={documentImage}
            aspect={1.75}
            label={t('oralExam.idCapture.review.documentLabel')}
            height={180}
          />
        </HStack>

        <FaceDoDont />

        {!!sendError && (
          <Text size="sm" color="$statusError" textAlign="center" testID="oral-exam-id-review-send-error">
            {sendError}
          </Text>
        )}

        <VStack space="sm" alignItems="center" width="100%">
          <Button
            size="md"
            width="100%"
            maxWidth={320}
            onPress={onSend}
            isDisabled={isSending}
            testID="oral-exam-id-review-send"
          >
            <ButtonText>{t('oralExam.idCapture.review.send')}</ButtonText>
          </Button>
          <HStack space="md">
            <Button size="sm" variant="outline" action="secondary" onPress={onRetakeFace}>
              <ButtonText>{t('oralExam.idCapture.review.retakeFace')}</ButtonText>
            </Button>
            <Button size="sm" variant="outline" action="secondary" onPress={onRetakeDocument}>
              <ButtonText>{t('oralExam.idCapture.review.retakeDocument')}</ButtonText>
            </Button>
          </HStack>
        </VStack>
      </VStack>
    </Center>
  );
}
